import React, { useEffect, useState, useCallback } from 'react';
import { SortableContainer, SortableElement } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import PhotoLibraryOutlinedIcon from '@material-ui/icons/PhotoLibraryOutlined';
import ReplayIcon from '@material-ui/icons/Replay';

export interface SortableSliderProps {
  data: {
    sliderItems: { imgSrc: string; id: string }[];
  };
  eventHandlers: {
    deleteHandler: (id: string) => void;
    sortHandler: (item: { imgSrc: string; id: string }[]) => void;
  };
}

interface PhotoItem {
  src: string;
  id: string;
  order: number;
}

const SortableItem = SortableElement<{
  value: PhotoItem;
  onRemove: (id: string) => void;
  order: number;
}>(
  ({
    value,
    onRemove,
    order
  }: {
    value: PhotoItem;
    onRemove: (id: string) => void;
    order: number;
  }) => (
    <div className="group relative aspect-square rounded-xl overflow-hidden bg-gray-100 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-xl transition-all duration-200 ring-1 ring-gray-200 hover:ring-primary-300">
      {/* Image */}
      <img
        src={value.src}
        alt={`Gallery item ${order}`}
        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
      />

      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />

      {/* Order Badge - top left */}
      <span className="absolute top-2 left-2 size-7 flex items-center justify-center bg-primary-500 backdrop-blur-sm text-white font-bold rounded-full text-sm shadow-md">
        {order}
      </span>

      {/* Drag Handle - top right on hover */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-white drop-shadow-md">
        {/* Delete Button - bottom right on hover */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(value.id);
          }}
          className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-0.5 size-10  bg-white/90 backdrop-blur-sm text-neutral-500 hover:text-red-500 hover:bg-red-50 rounded-lg shadow-md flex items-center justify-center"
          title="Remove image"
        >
          <CloseIcon style={{ fontSize: 12 }} />
        </button>
      </div>
    </div>
  )
);

const SortableList = SortableContainer<{ items: PhotoItem[]; onRemove: (id: string) => void }>(
  ({ items, onRemove }: { items: PhotoItem[]; onRemove: (id: string) => void }) => {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items?.map((value, index) => (
          <SortableItem
            key={`item-${value.id}`}
            index={index}
            value={value}
            onRemove={onRemove}
            order={index + 1}
          />
        ))}
      </div>
    );
  }
);

const SortableGalleryComp: React.FC<SortableSliderProps> = ({ data, eventHandlers }) => {
  const [photos, setPhotos] = useState<PhotoItem[]>([]);
  const [originalOrder, setOriginalOrder] = useState<string[]>([]);
  const [pendingOrder, setPendingOrder] = useState<PhotoItem[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    const mapped = (data.sliderItems as any[]).map((photo, index) => ({
      src: photo.imgSrc,
      id: photo.id,
      order: index + 1
    }));
    setPhotos(mapped);
    setPendingOrder(mapped);
    setOriginalOrder(mapped.map((p) => p.id));
    setHasChanges(false);
  }, [data]);

  const onSortEnd = useCallback(
    ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): void => {
      const newPhotos = arrayMoveImmutable(pendingOrder, oldIndex, newIndex);
      setPhotos(newPhotos);
      setPendingOrder(newPhotos);
      setHasChanges(true);
    },
    [pendingOrder]
  );

  const handleConfirm = useCallback(() => {
    const reorderedItems = pendingOrder.map((item, index) => ({
      imgSrc: item.src,
      id: item.id,
      order: index + 1
    }));
    eventHandlers.sortHandler(reorderedItems);
    setOriginalOrder(pendingOrder.map((p) => p.id));
    setHasChanges(false);
  }, [pendingOrder, eventHandlers]);

  const handleReset = useCallback(() => {
    const resetPhotos = photos
      .map((p) => ({
        ...p,
        order: originalOrder.indexOf(p.id) + 1
      }))
      .sort((a, b) => originalOrder.indexOf(a.id) - originalOrder.indexOf(b.id));

    setPhotos(resetPhotos);
    setPendingOrder(resetPhotos);
    setHasChanges(false);
  }, [photos, originalOrder]);

  const onRemove = useCallback(
    (id: string) => {
      eventHandlers.deleteHandler(id);
    },
    [eventHandlers]
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <PhotoLibraryOutlinedIcon style={{ fontSize: 18 }} />
          <span>
            {photos.length} image{photos.length !== 1 ? 's' : ''}
          </span>
        </div>
        {hasChanges && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleReset}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1.5 border border-gray-200"
            >
              <ReplayIcon style={{ fontSize: 16 }} />
              Reset
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className="px-4 py-1.5 text-sm bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-1.5 shadow-sm font-medium"
            >
              <CheckIcon style={{ fontSize: 16 }} />
              Save Order
            </button>
          </div>
        )}
      </div>

      {/* Grid or Empty State */}
      {photos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 px-4 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50/50">
          <PhotoLibraryOutlinedIcon style={{ fontSize: 48 }} className="text-gray-300 mb-3" />
          <p className="text-gray-400 text-sm font-medium">No images yet</p>
          <p className="text-gray-300 text-xs mt-1">Add images to get started</p>
        </div>
      ) : (
        <SortableList
          items={photos}
          onSortEnd={onSortEnd}
          axis="xy"
          onRemove={onRemove}
          helperClass="sorting-helper"
        />
      )}
    </div>
  );
};

export default SortableGalleryComp;
